import { TaskEffortBadge } from '@/components/badges/TaskEffortBadge';
import { SubscriptionCode } from '@/components/code/SubscriptionCode';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableHead } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { describeSubscription } from '@/lib/subscriptionCode';
import { cn } from '@/lib/utils';
import { latestProcessHowOftenResultsState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { formatDateTime } from '@/util/dates';
import type { ProcessHowOftenResult, TaskType } from '@rock/types';
import { Info } from 'lucide-react';
import type { FC } from 'react';

/** The headers for `TaskCadenceCells`, so every task table names and sizes them alike. */
export const TaskCadenceHeads: FC = () => (
    <>
        <TableHead className="min-w-[280px]">How Often</TableHead>
        <TableHead className="min-w-[90px]">Effort</TableHead>
    </>
);

/**
 * A task type's cadence as table cells: the subscription as parsed, with how it reads, the "How Often?" text
 * it came from, and the latest parse's notes on hover; then its effort.
 */
export const TaskCadenceCells: FC<{ taskType: TaskType }> = ({ taskType }) => {
    const latestParse = useAsyncAtomValue(latestProcessHowOftenResultsState)?.[taskType.id];
    return (
        <>
            <TableCell className="max-w-md whitespace-normal">
                <div className="flex items-center gap-1.5">
                    {taskType.subscription ? (
                        <SubscriptionCode subscription={taskType.subscription} />
                    ) : (
                        <Badge variant="outline">Not Set</Badge>
                    )}
                    <CadenceDetails taskType={taskType} latestParse={latestParse} />
                </div>
            </TableCell>
            <TableCell>
                <TaskEffortBadge effort={taskType.effort} />
            </TableCell>
        </>
    );
};

/** An info icon, amber when the latest parse left notes, that shows the rest of the cadence on hover. */
const CadenceDetails: FC<{ taskType: TaskType; latestParse: ProcessHowOftenResult | undefined }> = ({
    taskType,
    latestParse,
}) => {
    const issues = latestParse?.result.issues ?? [];
    const source = taskType.aiHowOftenInstructions?.trim();
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Info
                    aria-label="How this cadence was parsed"
                    className={cn(
                        'h-3.5 w-3.5 shrink-0 cursor-help',
                        issues.length ? 'text-amber-600' : 'text-muted-foreground',
                    )}
                />
            </TooltipTrigger>
            <TooltipContent className="text-pretty max-w-[360px] space-y-2 leading-snug">
                <div className="font-medium">
                    {taskType.subscription ? describeSubscription(taskType.subscription) : 'Not Set'}
                </div>
                <blockquote className="whitespace-pre-wrap border-l-2 border-slate-400 pl-2">
                    {source || <span className="opacity-70">No "How Often?" text</span>}
                </blockquote>
                {issues.map((issue) => (
                    <div key={issue} className="text-amber-300">
                        {issue}
                    </div>
                ))}
                {latestParse ? (
                    <div className="opacity-70">Latest parse, {formatDateTime(latestParse.createdAt)}</div>
                ) : null}
            </TooltipContent>
        </Tooltip>
    );
};
